import {
    Component,
    Injector,
    ViewChild,
    ViewEncapsulation,
    AfterViewInit,
    OnInit,
    OnDestroy,
    signal
} from '@angular/core';
import { appModuleAnimation } from '@axilla/axilla-shared';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    PagedResultDtoOfCustomerDto,
    WebShopServiceProxy,
    CustomerDto,
    PagedResultDtoOfUserListDto,
    UserListDto,
    VirtualBankAccountDto,
    ICreateVirtualBankUserInputDtoWithId,
    CurrencyDto, ICreateOrUpdateVirtualBankDto, UserEditDto
} from '@shared/service-proxies/service-proxies';
import { Paginator } from 'primeng/paginator';
import { Table } from 'primeng/table';
import {TreeNode} from 'primeng/api';
import {ImpersonationService} from '@app/admin/users/impersonation.service';
import {AppSessionService, UrlHelper} from '@axilla/axilla-shared';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {catchError, debounceTime, finalize, single, takeUntil} from 'rxjs/operators';
import {of} from 'rxjs';
import {FieldAgentService} from '@app/webshop/field-agent/shared/services/field-agent.service';
import {Subject} from '@node_modules/rxjs';
import {
    ViewAllManagersModalComponent
} from '@app/webshop/field-agent/modals/managers/view-all-managers-modal.component';
import {BsModalService} from 'ngx-bootstrap/modal';
import {AddManagerModalComponent} from '@app/webshop/field-agent/modals/managers/add-manager-modal.component';
import {EditCustomerModalComponent} from '@app/webshop/field-agent/modals/customer/edit-customer-modal.component';
import {CreateOrEditUserModalComponent} from '@app/admin/users/create-or-edit-user-modal.component';
import {BillingInfoValidator} from '@app/shared/common/billing-information/billing-information.component';

@Component({
    templateUrl: './select-customer.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./select-customer.component.less'],
    animations: [appModuleAnimation()]
})
export class SelectCustomerComponent extends AppComponentBase implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('viewAllManagersModal', {static: true}) viewAllManagersModal: ViewAllManagersModalComponent;
    @ViewChild('addManagerModal', {static: true}) addManagerModal: AddManagerModalComponent;
    @ViewChild('editCustomerModal', {static: true}) editCustomerModal: EditCustomerModalComponent;

    @ViewChild('createOrEditUserModal', { static: true }) createOrEditUserModal: CreateOrEditUserModalComponent;

    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    editCustomerOptions: any;

    protected readonly destroy$ = new Subject<void>(); // Used to signal unsubscription

    selectedCustomer: CustomerDto | undefined = undefined;

    get customerUsersValue(): UserListDto[] {
        return this._fieldAgentService.customerUsersValue;
    }

    get customerShortUsersValue(): UserListDto[] {
        return this._fieldAgentService.customerUsersShortValue;
    }

    get isCustomerUsersAvailable(): boolean {
        return this._fieldAgentService.isCustomerUsersAvailable;
    }

    get isShowViewAllManagers(): boolean {
        return this._fieldAgentService.isShowViewAllManagers;
    }

    //#region Search
    searchForm: UntypedFormGroup;

    //Filters
    hideAdvancedFilters = true;
    advancedFiltersAreShown = false;
    get filterText(): string {
        return this.searchForm.get('filter')?.value || '';
    }

    get isFilterEmpty(): boolean {
        const filterValue = this.searchForm.get('filter')?.value;
        return !filterValue || (typeof filterValue === 'string' && filterValue.trim() === '');
    }

    private _initsearchForm() {
        this.searchForm = new UntypedFormGroup({
            filter: new UntypedFormControl(null)
        });
    }

    clearInput() {
        this.searchForm.reset();
    }
    //#endregion Search

    //this is validation rules for create user modal
    billingInfoValidator: BillingInfoValidator | undefined;

    constructor(
        injector: Injector,
        public _impersonationService: ImpersonationService,
        private _sessionService: AppSessionService,
        private _fieldAgentService: FieldAgentService,
    ) {
        super(injector);

        this.billingInfoValidator = {
            requiredVisitorBlock: true,
            requiredInvoiceBlock: true,
            requiredDeliveryBlock: true,
            requiredInvoiceEAddress: true
        } as BillingInfoValidator;
    }

    ngOnInit() {
        this._initsearchForm();

        this.editCustomerOptions = {
            requiredLegalName: true,
            requiredContactName: true,
            requiredAddress: true,
            requiredAddressHouseNumber: true,
            requiredAddressApartment: true,
            requiredAddressPostcode: true,
            requiredAddressCode: true,
            requiredAddressCity: true,
            requiredAddressCountry: true,
            requiredDeliveryContactName: false,
            requiredDeliveryAddress: false,
            requiredDeliveryAddressHouseNumber: false,
            requiredDeliveryAddressApartment: false,
            requiredDeliveryAddressPostcode: false,
            requiredDeliveryAddressCode: false,
            requiredDeliveryAddressCity: false,
            requiredDeliveryAddressCountry: false,
            requiredInvoiceContactName: false,
            requiredInvoiceAddress: false,
            requiredInvoiceAddressHouseNumber: false,
            requiredInvoiceAddressApartment: false,
            requiredInvoiceAddressPostcode: false,
            requiredInvoiceAddressCode: false,
            requiredInvoiceAddressCity: false,
            requiredInvoiceAddressCountry: false
        };

        this.searchForm.get('filter').valueChanges
            .pipe(
                debounceTime(300),
                catchError(error => of(`Caught an error: ${error.message}`)),
                takeUntil(this.destroy$)
            ).subscribe({
                next: query => this.getCustomers(),
                error: err => `Error: ${err.message}`,
                complete: () => console.log('Completed')
            });

        this._fieldAgentService.selectedCustomer$
            .pipe(
                takeUntil(this.destroy$) // Automatically unsubscribes when destroy$ emits
            )
            .subscribe(selectedCustomer => {
                if (selectedCustomer) {
                    this.getCustomerUsers();
                }
            });
    }

    ngAfterViewInit(): void {
        this.primengTableHelper.adjustScroll(this.dataTable);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCustomerSelect(event: any) {
        if (this.selectedCustomer) {
            this._fieldAgentService.selectCustomer(this.selectedCustomer);
        }
    }

    onCustomerUnselect(event: any) {
        this._fieldAgentService.unselectCustomer();
    }

    getCustomers(event?: any) {
        if (this.primengTableHelper.shouldResetPaging(event)) {
            this.paginator.changePage(0);
            // return;
        }

        this.primengTableHelper.showLoadingIndicator();

        this._fieldAgentService.getCustomers$(
            this.filterText,
            this.primengTableHelper.getSorting(this.dataTable),
            this.primengTableHelper.getSkipCount(this.paginator, event),
            this.primengTableHelper.getMaxResultCount(this.paginator, event)
        )
        .pipe(
            finalize(() => this.primengTableHelper.hideLoadingIndicator())
        )
        .subscribe((result: PagedResultDtoOfCustomerDto) => {
            this.primengTableHelper.totalRecordsCount = result.totalCount;
            this.primengTableHelper.records = result.items;
            this.primengTableHelper.hideLoadingIndicator();
        });
    }

    getCustomerUsers() {
        this._fieldAgentService.getAllCustomerUsers$(undefined, undefined, 0, 100)
            .subscribe();
    }

    reloadPage(): void {
        this.paginator.changePage(this.paginator.getPage());
    }

    openAddressInfoModal(selectedOUs: TreeNode[]) {
        //this.createOrEditUserModal.openAndAddSelectedOUs(selectedOUs);
    }

    editCustomer(selectedCustomer: CustomerDto) {
        this._fieldAgentService.getCustomerForEdit$(selectedCustomer.id)
            .pipe(
                finalize(() => this.primengTableHelper.hideLoadingIndicator())
            )
            .subscribe((result: VirtualBankAccountDto) => {
                const input = {
                    id: result.id, // Assign the correct value if required
                    userId: selectedCustomer.id,
                    legalName: result.legalName,
                    contactEmailAddress: result.contactEmailAddress,
                    contactName: result.contactName,
                    address: result.address,
                    addressHouseNumber: result.addressHouseNumber,
                    addressApartment: result.addressApartment,
                    addressPostcode: result.addressPostcode,
                    addressCode: result.addressCode,
                    addressCity: result.addressCity,
                    addressCountry: result.addressCountry,
                    addressComment: result.addressComment,
                    invoiceContactName: result.invoiceContactName,
                    invoiceLegalName: result.legalName, // Assuming legalName applies to invoices
                    invoiceAddress: result.invoiceAddress,
                    invoiceAddressHouseNumber: result.invoiceAddressHouseNumber,
                    invoiceAddressApartment: result.invoiceAddressApartment,
                    invoiceAddressPostcode: result.invoiceAddressPostcode,
                    invoiceAddressCode: result.invoiceAddressCode,
                    invoiceAddressCity: result.invoiceAddressCity,
                    invoiceAddressCountry: result.invoiceAddressCountry,
                    invoiceAddressComment: '', // Assign a default value or map from result
                    deliveryContactName: result.deliveryContactName,
                    deliveryLegalName: result.legalName, // Assuming legalName applies to deliveries
                    deliveryAddress: result.deliveryAddress,
                    deliveryAddressHouseNumber: result.deliveryAddressHouseNumber,
                    deliveryAddressApartment: result.deliveryAddressApartment,
                    deliveryAddressPostcode: result.deliveryAddressPostcode,
                    deliveryAddressCode: result.deliveryAddressCode,
                    deliveryAddressCity: result.deliveryAddressCity,
                    deliveryAddressCountry: result.deliveryAddressCountry,
                    deliveryAddressComment: '', // Assign a default value or map from result
                    externalUserReference: result.externalUserReference,
                    phoneAvailability: result.phoneAvailability,
                    phoneNumber: result.phoneNumber,
                    phoneNumberSecondary: result.phoneNumberSecondary,
                    registrationNo: result.registrationNo,
                    personalRegistrationNo: result.personalRegistrationNo,
                    accountNumber: result.accountNumber,
                    accountBankName: result.accountBankName,
                    accountSwift: result.accountSwift,
                    accountBic: result.accountBic,
                    paymentTerms: result.paymentTerms,
                    accountRsin: result.accountRsin,
                    invoiceEAddress: result.invoiceEAddress,
                    invoiceRememberEInvoiceAddress: result.invoiceRememberEInvoiceAddress
                } as ICreateOrUpdateVirtualBankDto;

                this.editCustomerModal.show(input);
            });
    }

    viewAllCustomerUsers() {
        this.viewAllManagersModal.show();
    }

    addNewManager() {
        this.addManagerModal.show();
    }

    onManagerSelect($event: any) {
        abp.message.confirm(
            'Do you want to use this user?'
            , 'Impersonation with user: ' + this._getFullName($event)
            , (result: boolean) => {
                if (result) {
                    this._impersonationService.impersonate($event.id, this._sessionService.tenantId);
                }
            }
            , {
                showCloseButton: true,
                customClass: {
                    container: 'modal-like'
                }
            });
    }

    private _getFullName(user: any) {
        if (!user) {
            return ''; // Return an empty string if user is undefined
        }

        let firstName = '';
        if ('firstName' in user) {
            firstName = user.firstName || '';
        } else if ('name' in user) {
            firstName = user.name || '';
        }

        // Combine 'name' and 'surname' to create full name
        return `${firstName} ${user.surname || ''}`.trim();
    }

    onCreateManager($event: any) {
        this.getCustomerUsers();
    }

    onEditCustomer($event: any) {
        this.getCustomers();

        this._unselectCustomer();
    }

    private _unselectCustomer() {
        this._fieldAgentService.unselectCustomer();
        this.selectedCustomer = undefined;
    }

    //#region Create user
    openSelectOU(user: UserEditDto) {
        //this.selectOrganizationUnitModal.show(user);
    }

    createUser(): void {
        this.createOrEditUser();
    }

    createOrEditUser(email?: string) {
        this.createOrEditUserModal.show(undefined, undefined, email);
    }
    //#endregion Create user

}
